import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { botIntructions } from '../../services/data'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

const LIMIT = 1000

const ensureGlobalConfig = () => {
  if (!window.chatbotConfig) window.chatbotConfig = {}
  if (!('ai_instruction' in window.chatbotConfig))
    window.chatbotConfig.ai_instruction = ''
}

const normalizeAIText = raw =>
  String(raw ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trimStart()

export default function BotInstructionsTab ({
  urlstoreid,
  storeId,
  limitedPermission,
  onSavePartial
}) {
  const resolvedStoreId = useMemo(
    () => urlstoreid || storeId || '',
    [urlstoreid, storeId]
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [aiInstruction, setAiInstruction] = useState('')
  const [isEdited, setIsEdited] = useState(false)

  useEffect(() => {
    ensureGlobalConfig()
    setAiInstruction(normalizeAIText(window.chatbotConfig.ai_instruction || ''))
  }, [])

  const persistLocal = useCallback(text => {
    ensureGlobalConfig()
    window.chatbotConfig.ai_instruction = text
  }, [])

  const handleChange = e => {
    const next = e.target.value
    setAiInstruction(next)
    setIsEdited(true)
  }

  const handleGenerateAI = async () => {
    setError(null)
    if (!resolvedStoreId) return
    try {
      setLoading(true)
      const data = await botIntructions({ store_id: resolvedStoreId })
      if (!data) throw new Error('Failed to generate')

      if (!data?.text) {
        toast.warning('Please try again to generate AI instruction.', {
          position: 'bottom-center',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        })
        return
      }

      const fromAPI = normalizeAIText(data.text)
      setAiInstruction(fromAPI || '')
      setIsEdited(true)
    } catch (err) {
      setError(err?.message || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = () => {
    if (!aiInstruction.trim()) return
    persistLocal(aiInstruction)
    onSavePartial?.({ ai_instruction: aiInstruction })
    setIsEdited(false)

    toast.success('Instructions saved successfully!', {
      position: 'bottom-center',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true
    })
  }

  const handleClear = () => {
    setAiInstruction('')
    persistLocal('')
    setIsEdited(true)
  }

  return (
    <>
      <div className='rounded-lg border border-gray-200 bg-white p-4 space-y-4 relative'>

        {/* Textarea */}
        <div>
          <textarea
            className='w-full min-h-[180px] rounded-md border border-gray-300 p-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#7C50DD] focus:border-transparent'
            value={aiInstruction}
            onChange={handleChange}
            maxLength={LIMIT}
            spellCheck={true}
          />
          <p className='mt-2 text-xs text-gray-600'>
            Describe your assistant’s tone, personality, and behavior when
            talking to customers.
          </p>
          {error && <p className='mt-2 text-xs text-red-600'>{error}</p>}

          {/* Moved instructional text below textarea */}
          <div className='mt-3 text-xs text-gray-500'>
            Click <span className='font-medium'>Generate using AI</span> to
            automatically create instructions for your AI assistant. You can
            then review and edit them before saving.
          </div>
        </div>

        {/* Buttons at bottom-right */}
        <div className='flex justify-end gap-3 mt-4'>
          {/* Generate button */}
          <button
            type='button'
            onClick={handleGenerateAI}
            className='px-10 py-2 bg-[#7C50DD] flex justify-center items-center text-white rounded-full text-[14px] disabled:opacity-50'
            disabled={loading}
          >
            {loading ? 'Generating…' : 'Generate using AI'}
          </button>

          {/* Save button */}
          <button
            type='button'
            onClick={handleSave}
            disabled={!isEdited || !aiInstruction.trim()}
            className={`px-10 py-2 flex justify-center items-center rounded-full text-white text-[14px] ${
              isEdited && aiInstruction.trim()
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Save Instructions
          </button>

          {/* Clear button */}
          <button
            type='button'
            onClick={handleClear}
            disabled={!aiInstruction.trim()}
            className={`px-10 py-2 flex justify-center items-center rounded-full border text-[14px] ${
              aiInstruction.trim()
                ? 'bg-white border-gray-300 hover:bg-gray-100'
                : 'bg-white border-gray-200 cursor-not-allowed'
            }`}
          >
            Clear
          </button>
        </div>
      </div>

      <ToastContainer />
    </>
  )
}
